import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import Tag from "@/components/base/Tag";
import WorkCard from "@/components/feature/WorkCard";
import { worksList } from "@/mocks/works";
import GalleryCarousel from "@/pages/works/detail/components/GalleryCarousel";

export default function WorkDetailPage() {
  const { id } = useParams<{ id: string }>();
  const work = useMemo(() => worksList.find((w) => w.id === id), [id]);

  const related = useMemo(() => {
    if (!work) return [];
    const overlap = (genres: string[]) => genres.filter((g) => work.genres.includes(g)).length;
    return worksList
      .filter((w) => w.id !== work.id)
      .sort((a, b) => {
        const scoreA = overlap(a.genres) * 3 + (a.category === work.category ? 2 : 0) - Math.abs(a.year - work.year) * 0.5;
        const scoreB = overlap(b.genres) * 3 + (b.category === work.category ? 2 : 0) - Math.abs(b.year - work.year) * 0.5;
        return scoreB - scoreA;
      })
      .slice(0, 3);
  }, [work]);

  useSeo({
    title: work ? `${work.title} - 详细介绍|佐佐木明希` : "作品详情 - 佐佐木明希官网",
    description: work
      ? `${work.title}（${work.year}${work.category}）由佐佐木明希${work.roleType}出演，饰演${work.role}。本页提供剧情概要、角色介绍、主创信息与作品剧照。`
      : "佐佐木明希影视作品详情页。",
    keywords: work
      ? `${work.title},佐佐木明希,${work.category},${work.role},${work.genres.join(",")}`
      : "佐佐木明希,影视作品",
  });

  if (!work) {
    return (
      <section className="bg-background-50 pb-20 pt-32">
        <div className="mx-auto w-full max-w-[1200px] px-4 md:px-6">
          <div className="rounded-lg border border-dashed border-background-300 bg-background-100 py-24 text-center">
            <i className="ri-error-warning-line text-4xl text-foreground-400" aria-hidden="true" />
            <p className="mt-4 font-heading text-lg text-foreground-800">作品不存在或已下架</p>
            <p className="mt-2 text-sm text-foreground-500">该作品可能已从本站移除，或链接不正确</p>
            <Link
              to="/works"
              className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-arrow-left-line" aria-hidden="true" />
              返回影视作品列表
            </Link>
          </div>
        </div>
      </section>
    );
  }

  const galleryImages = [work.cover, ...work.stills];

  return (
    <>
      <section className="bg-background-100 pb-12 pt-28 md:pb-16 md:pt-36">
        <div className="mx-auto w-full max-w-[1200px] px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: "首页", path: "/" },
              { label: "影视作品", path: "/works" },
              { label: work.title },
            ]}
          />

          <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
            <div className="lg:col-span-5">
              <div className="mx-auto w-full max-w-sm overflow-hidden rounded-lg lg:max-w-none">
                <div className="relative w-full pt-[133%]">
                  <img
                    src={work.cover}
                    alt={`${work.title} 海报封面 - 佐佐木明希${work.roleType}作品`}
                    title={`${work.title} 佐佐木明希 影视作品`}
                    className="absolute inset-0 h-full w-full object-cover object-top"
                  />
                </div>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="flex flex-wrap items-center gap-2">
                <Tag tone={work.category === "电影" ? "accent" : "primary"}>{work.category}</Tag>
                <Tag tone="secondary">{work.roleType}</Tag>
                <Tag tone="neutral">{work.year} 年</Tag>
              </div>
              <h1 className="mt-4 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-4xl lg:text-5xl">
                {work.title}
              </h1>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                {work.synopsis}
              </p>

              <dl className="mt-7 grid grid-cols-1 gap-x-8 gap-y-4 border-t border-background-200 pt-6 sm:grid-cols-2">
                <div>
                  <dt className="text-xs font-label uppercase tracking-wider text-foreground-500">上映年份</dt>
                  <dd className="mt-1 text-sm text-foreground-900">{work.year} 年</dd>
                </div>
                <div>
                  <dt className="text-xs font-label uppercase tracking-wider text-foreground-500">作品类型</dt>
                  <dd className="mt-1 text-sm text-foreground-900">{work.category}</dd>
                </div>
                <div>
                  <dt className="text-xs font-label uppercase tracking-wider text-foreground-500">导演</dt>
                  <dd className="mt-1 text-sm text-foreground-900">{work.director}</dd>
                </div>
                <div>
                  <dt className="text-xs font-label uppercase tracking-wider text-foreground-500">角色类型</dt>
                  <dd className="mt-1 text-sm text-foreground-900">
                    {work.roleType} · 饰 {work.role}
                  </dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-xs font-label uppercase tracking-wider text-foreground-500">主要演员</dt>
                  <dd className="mt-2 flex flex-wrap gap-2">
                    {work.cast.map((name) => (
                      <span
                        key={name}
                        className={`rounded-full px-3 py-1 text-xs ${
                          name === "佐佐木明希"
                            ? "bg-primary-100 text-primary-800"
                            : "bg-background-200 text-foreground-700"
                        }`}
                      >
                        {name}
                      </span>
                    ))}
                  </dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-xs font-label uppercase tracking-wider text-foreground-500">题材标签</dt>
                  <dd className="mt-2 flex flex-wrap gap-2">
                    {work.genres.map((genre) => (
                      <Tag key={genre} tone="secondary">
                        {genre}
                      </Tag>
                    ))}
                  </dd>
                </div>
              </dl>

              <div className="mt-7 flex flex-wrap items-center gap-4">
                <span className="inline-flex items-center gap-2 rounded-md bg-background-50 px-4 py-2 text-sm text-foreground-800">
                  <i className="ri-star-fill text-accent-500" aria-hidden="true" />
                  评分 {work.rating.toFixed(1)}
                </span>
                <span className="inline-flex items-center gap-2 rounded-md bg-background-50 px-4 py-2 text-sm text-foreground-800">
                  <i className="ri-fire-line text-accent-500" aria-hidden="true" />
                  热度 {work.popularity}
                </span>
                <Link
                  to="/works"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-label font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-700"
                >
                  <i className="ri-arrow-left-line" aria-hidden="true" />
                  返回影视作品列表
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-50 py-14 md:py-16">
        <div className="mx-auto grid w-full max-w-[1200px] grid-cols-1 gap-10 px-4 md:px-6 lg:grid-cols-3 lg:gap-12">
          <article className="lg:col-span-2">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">剧情概要</h2>
            <p className="mt-4 text-sm leading-loose text-foreground-700 md:text-base">{work.synopsis}</p>

            <h2 className="mt-10 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              角色介绍 · {work.role}
            </h2>
            <p className="mt-4 text-sm leading-loose text-foreground-700 md:text-base">
              {work.characterIntro}
            </p>

            <h2 className="mt-10 font-heading text-xl font-bold text-foreground-950 md:text-2xl">作品剧照</h2>
            <div className="mt-5">
              <GalleryCarousel images={galleryImages} title={work.title} />
            </div>
          </article>

          <aside className="lg:col-span-1">
            <div className="rounded-lg border border-background-200 bg-background-100 p-6">
              <h3 className="font-heading text-base font-bold text-foreground-950">作品速览</h3>
              <ul className="mt-4 space-y-3 text-sm">
                <li className="flex items-center justify-between gap-4">
                  <span className="text-foreground-500">片名</span>
                  <span className="text-right text-foreground-900">{work.title}</span>
                </li>
                <li className="flex items-center justify-between gap-4">
                  <span className="text-foreground-500">类型</span>
                  <span className="text-right text-foreground-900">{work.category}</span>
                </li>
                <li className="flex items-center justify-between gap-4">
                  <span className="text-foreground-500">年份</span>
                  <span className="text-right text-foreground-900">{work.year}</span>
                </li>
                <li className="flex items-center justify-between gap-4">
                  <span className="text-foreground-500">明希角色</span>
                  <span className="text-right text-foreground-900">{work.role}</span>
                </li>
                <li className="flex items-center justify-between gap-4">
                  <span className="text-foreground-500">导演</span>
                  <span className="text-right text-foreground-900">{work.director}</span>
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </section>

      {related.length > 0 && (
        <section className="bg-background-100 py-14 md:py-16">
          <div className="mx-auto w-full max-w-[1200px] px-4 md:px-6">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">相关推荐</h2>
            <p className="mt-2 text-sm text-foreground-600">
              根据《{work.title}》的类型与年份为你推荐以下作品
            </p>
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((item) => (
                <WorkCard key={item.id} item={item} />
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}